/**
 * The client audio engine (AD-861, docs/architecture/client-audio.md). Web Audio graph:
 *
 *   sfx voices ──► sfx bus ─┐
 *   voice lines ─► voice bus ┼─► master ─► destination
 *   music A/B ───► music bus ┘
 *
 * - Locked until the first user gesture (`unlock()` from inside the gesture handler): nothing is
 *   created, fetched or played before it, so iOS/Android autoplay rules are met by construction.
 * - SFX are fetched + decoded lazily (per-zone preload after unlock) into a decoded-byte budget;
 *   over budget, the least-recently-played buffer is dropped (low tier: a smaller budget).
 * - Music streams through a media element (never fully decoded — a minute of decoded stereo is
 *   tens of MB) and crossfades between two slots on a state change.
 * - A missing or undecodable file is SILENT: logged once to the cue log, never thrown.
 *
 * Every platform object comes in through `AudioPorts`, so the engine is unit-tested in Node.
 */
import type { CueMap, MusicState } from './cueMap.js';
import { fileUrl } from './cueMap.js';
import type { AudioBus, AudioSettings } from './settings.js';
import { busGain } from './settings.js';

// ---- the slice of Web Audio the engine uses (structural, so tests can fake it) ----------------
export interface ParamLike {
  value: number;
  setValueAtTime(v: number, t: number): void;
  linearRampToValueAtTime(v: number, t: number): void;
  cancelScheduledValues(t: number): void;
}
export interface NodeLike {
  connect(dest: NodeLike): void;
  disconnect(): void;
}
export interface GainLike extends NodeLike {
  gain: ParamLike;
}
export interface BufferLike {
  length: number;
  numberOfChannels: number;
  duration: number;
}
export interface SourceLike extends NodeLike {
  buffer: BufferLike | null;
  start(when?: number): void;
  stop(when?: number): void;
  onended: (() => void) | null;
}
export interface OscLike extends NodeLike {
  type: string;
  frequency: ParamLike;
  start(when?: number): void;
  stop(when?: number): void;
}
export interface MediaLike {
  src: string;
  loop: boolean;
  preload: string;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: 'error' | 'ended', fn: () => void): void;
}
export interface ContextLike {
  state: string;
  currentTime: number;
  destination: NodeLike;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  createGain(): GainLike;
  createBufferSource(): SourceLike;
  createOscillator(): OscLike;
  createBuffer(channels: number, length: number, rate: number): BufferLike;
  decodeAudioData(data: ArrayBuffer): Promise<BufferLike>;
  createMediaElementSource(el: MediaLike): NodeLike;
}

export interface AudioPorts {
  /** Construct the AudioContext (called once, inside the unlocking gesture). Null = no Web Audio. */
  createContext(): ContextLike | null;
  /** Fetch a file's bytes; null for a 404 / network failure (never throws). */
  fetchBytes(url: string): Promise<ArrayBuffer | null>;
  /** A fresh media element for music, or null where none can exist. */
  createMedia(): MediaLike | null;
  /** True when the browser can play Ogg Opus (else the m4a/AAC fallback is used). */
  canPlayOgg(): boolean;
  now(): number;
}

export type CueOutcome =
  | 'played'
  | 'locked'
  | 'muted'
  | 'missing'
  | 'unknown'
  | 'throttled'
  | 'loading'
  | 'voices';

export interface CueLogEntry {
  /**
   * 1, 2, 3 … for the engine's life, never reused. The log keeps only the last LOG_CAP entries, so
   * its LENGTH stops moving once full; a reader marks "since now" by the last seq, not the length
   * (q-sel-lore-voice-lines: the lane's index mark read an empty slice late in a run).
   */
  seq: number;
  t: number;
  cue: string;
  outcome: CueOutcome;
}

export interface EngineOptions {
  /** Decoded-PCM budget in bytes (low tier smaller: design §4). */
  decodedBudgetBytes: number;
  /** Max simultaneous SFX voices. */
  maxVoices: number;
  /** Same cue retriggered within this window is dropped (stacking guard). */
  retriggerMs: number;
  /** A lazily-loaded cue still plays if its decode lands within this window, else it is dropped. */
  lateMs: number;
  /** Music crossfade length, seconds. */
  crossfadeSec: number;
  baseUrl: string;
}

export const DEFAULT_ENGINE_OPTIONS: Readonly<EngineOptions> = Object.freeze({
  decodedBudgetBytes: 8 * 1024 * 1024,
  maxVoices: 12,
  retriggerMs: 45,
  lateMs: 250,
  crossfadeSec: 1.5,
  baseUrl: '/audio',
});

/** Low-tier (phone) budget: design §4. */
export const LOW_TIER_DECODED_BUDGET_BYTES = 2 * 1024 * 1024;
export const LOW_TIER_MAX_VOICES = 6;

export const LOG_CAP = 200;

/** Decoded bytes of a buffer (Web Audio holds float32 PCM per channel). */
export const decodedBytes = (b: BufferLike): number => b.length * b.numberOfChannels * 4;

interface CachedBuffer {
  buffer: BufferLike;
  bytes: number;
  lastUsed: number;
}

interface MusicSlot {
  media: MediaLike;
  gain: GainLike;
  cue: string;
}

export class AudioEngine {
  private ctx: ContextLike | null = null;
  private buses: Partial<Record<AudioBus, GainLike>> = {};
  private readonly cache = new Map<string, CachedBuffer>();
  private readonly pending = new Map<string, Promise<BufferLike | null>>();
  private readonly missing = new Set<string>();
  private readonly lastPlayed = new Map<string, number>();
  private readonly variant = new Map<string, number>();
  private voices = 0;
  private voiceLine: { src: SourceLike; g: GainLike } | null = null;
  private voiceToken = 0;
  private music: MusicSlot | null = null;
  private musicState: MusicState | null = null;
  private readonly log: CueLogEntry[] = [];
  private logSeq = 0;
  private ext: 'ogg' | 'm4a' = 'ogg';
  readonly options: EngineOptions;

  constructor(
    private readonly map: CueMap,
    private readonly ports: AudioPorts,
    private settings: AudioSettings,
    options: Partial<EngineOptions> = {},
  ) {
    this.options = { ...DEFAULT_ENGINE_OPTIONS, ...options };
  }

  /**
   * The render tier is known only once Game resolves its quality profile, so the budget is set
   * at unlock rather than at construction. Low tier: the smaller decoded budget and voice cap.
   */
  configureTier(low: boolean): void {
    this.options.decodedBudgetBytes = low
      ? LOW_TIER_DECODED_BUDGET_BYTES
      : DEFAULT_ENGINE_OPTIONS.decodedBudgetBytes;
    this.options.maxVoices = low ? LOW_TIER_MAX_VOICES : DEFAULT_ENGINE_OPTIONS.maxVoices;
    this.enforceBudget('');
  }

  get unlocked(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Bytes of decoded PCM currently held. */
  get decodedBytesHeld(): number {
    let n = 0;
    for (const c of this.cache.values()) n += c.bytes;
    return n;
  }

  get currentMusicState(): MusicState | null {
    return this.musicState;
  }

  cueLog(): readonly CueLogEntry[] {
    return this.log;
  }

  private note(cue: string, outcome: CueOutcome): void {
    this.log.push({ seq: ++this.logSeq, t: this.ports.now(), cue, outcome });
    if (this.log.length > LOG_CAP) this.log.splice(0, this.log.length - LOG_CAP);
  }

  /**
   * Call from INSIDE a user-gesture handler. Creates the context on the first call, resumes it,
   * and plays a one-sample silent buffer (the legacy iOS unlock). Idempotent; returns true once
   * the context is running.
   */
  async unlock(): Promise<boolean> {
    if (!this.ctx) {
      const ctx = this.ports.createContext();
      if (!ctx) return false;
      this.ctx = ctx;
      this.ext = this.ports.canPlayOgg() ? 'ogg' : 'm4a';
      const master = ctx.createGain();
      master.connect(ctx.destination);
      this.buses.master = master;
      for (const bus of ['music', 'sfx', 'voice'] as const) {
        const g = ctx.createGain();
        g.connect(master);
        this.buses[bus] = g;
      }
      this.applySettings(this.settings);
      try {
        const silent = ctx.createBufferSource();
        silent.buffer = ctx.createBuffer(1, 1, 22050);
        silent.connect(master);
        silent.start(0);
      } catch {
        /* a context that refuses the silent buffer still resumes below */
      }
    }
    if (this.ctx.state !== 'running') {
      try {
        await this.ctx.resume();
      } catch {
        return false;
      }
    }
    return this.ctx.state === 'running';
  }

  /** Page hidden: suspend (phones stop background audio anyway). Visible again: resume. */
  async setHidden(hidden: boolean): Promise<void> {
    if (!this.ctx) return;
    try {
      if (hidden) await this.ctx.suspend();
      else await this.ctx.resume();
    } catch {
      /* best effort */
    }
  }

  applySettings(settings: AudioSettings): void {
    this.settings = settings;
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const bus of ['master', 'music', 'sfx', 'voice'] as const) {
      const g = this.buses[bus];
      if (!g) continue;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(busGain(settings, bus), t);
    }
  }

  private load(file: string): Promise<BufferLike | null> {
    const hit = this.cache.get(file);
    if (hit) return Promise.resolve(hit.buffer);
    if (this.missing.has(file)) return Promise.resolve(null);
    const inflight = this.pending.get(file);
    if (inflight) return inflight;
    const ctx = this.ctx;
    if (!ctx) return Promise.resolve(null);
    const p = (async () => {
      const bytes = await this.ports.fetchBytes(
        fileUrl('sfx', file, this.ext, this.options.baseUrl),
      );
      let buffer: BufferLike | null = null;
      if (bytes) {
        try {
          buffer = await ctx.decodeAudioData(bytes);
        } catch {
          buffer = null;
        }
      }
      this.pending.delete(file);
      if (!buffer) {
        this.missing.add(file);
        this.note(`file:${file}`, 'missing');
        return null;
      }
      this.cache.set(file, { buffer, bytes: decodedBytes(buffer), lastUsed: this.ports.now() });
      this.enforceBudget(file);
      return buffer;
    })();
    this.pending.set(file, p);
    return p;
  }

  /** Fetch + decode a spoken line. Not cached: the HTTP cache serves a replay; decoded PCM is freed after the line. */
  private async loadVoice(file: string): Promise<BufferLike | null> {
    const ctx = this.ctx;
    if (!ctx || this.missing.has(file)) return null;
    const bytes = await this.ports.fetchBytes(
      fileUrl('voice', file, this.ext, this.options.baseUrl),
    );
    let buffer: BufferLike | null = null;
    if (bytes) {
      try {
        buffer = await ctx.decodeAudioData(bytes);
      } catch {
        buffer = null;
      }
    }
    if (!buffer) {
      this.missing.add(file);
      this.note(`file:${file}`, 'missing');
    }
    return buffer;
  }

  /**
   * Speak a voice-bus cue (one line at a time: a new line cuts the old one). `onEnd` fires when the
   * line finishes, or at once when it cannot play (locked, muted, missing), so a playlist moves on.
   * A line cut by `stopVoice()` or by the next `speak()` never calls its `onEnd`.
   */
  speak(cue: string, onEnd: () => void): CueOutcome {
    this.stopVoice();
    const token = this.voiceToken;
    const def = this.map.sfx[cue];
    const file = def?.bus === 'voice' ? def.files?.[0] : undefined;
    let outcome: CueOutcome;
    if (!def || def.bus !== 'voice') outcome = 'unknown';
    else if (!this.unlocked || !this.ctx) outcome = 'locked';
    else if (this.settings.master.muted || this.settings.voice.muted) outcome = 'muted';
    else if (!file || this.missing.has(file)) outcome = 'missing';
    else outcome = 'loading';
    if (outcome !== 'loading' || !def || !file) {
      this.note(cue, outcome);
      onEnd();
      return outcome;
    }
    this.note(cue, 'loading');
    void this.loadVoice(file).then((buffer) => {
      if (token !== this.voiceToken) return; // cut or replaced while it loaded
      if (!buffer) {
        onEnd();
        return;
      }
      this.startVoice(buffer, def.gain, onEnd);
      this.note(cue, 'played');
    });
    return outcome;
  }

  private startVoice(buffer: BufferLike, gain: number, onEnd: () => void): void {
    const ctx = this.ctx;
    const out = this.buses.voice;
    if (!ctx || !out) return onEnd();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(out);
    const line = { src, g };
    this.voiceLine = line;
    src.onended = () => {
      if (this.voiceLine !== line) return;
      this.voiceLine = null;
      src.disconnect();
      g.disconnect();
      onEnd();
    };
    src.start(0);
  }

  /** Cut the line being spoken (and any still loading). Safe when nothing is speaking. */
  stopVoice(): void {
    this.voiceToken += 1;
    const line = this.voiceLine;
    if (!line) return;
    this.voiceLine = null;
    line.src.onended = null;
    try {
      line.src.stop(0);
    } catch {
      /* already ended */
    }
    line.src.disconnect();
    line.g.disconnect();
  }

  /** Drop least-recently-used buffers until within budget (never the one just loaded). */
  private enforceBudget(keep: string): void {
    let held = this.decodedBytesHeld;
    if (held <= this.options.decodedBudgetBytes) return;
    const order = [...this.cache.entries()]
      .filter(([k]) => k !== keep)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [k, c] of order) {
      if (held <= this.options.decodedBudgetBytes) break;
      this.cache.delete(k);
      held -= c.bytes;
    }
  }

  /** Fetch + decode the files of these cues ahead of need (after unlock; no-op before). */
  async preload(cues: readonly string[]): Promise<void> {
    if (!this.ctx) return;
    const files = cues.flatMap((c) => this.map.sfx[c]?.files ?? []);
    await Promise.all(files.map((f) => this.load(f)));
  }

  /** Play an SFX cue. Never throws; the outcome lands in the cue log. */
  play(cue: string): CueOutcome {
    const outcome = this.playInner(cue);
    this.note(cue, outcome);
    return outcome;
  }

  private playInner(cue: string): CueOutcome {
    const def = this.map.sfx[cue];
    if (!def) return 'unknown';
    if (def.bus === 'voice') return 'unknown'; // spoken lines go through speak()
    if (!this.unlocked || !this.ctx) return 'locked';
    if (this.settings.master.muted || this.settings[def.bus].muted) return 'muted';
    const now = this.ports.now();
    const last = this.lastPlayed.get(cue);
    if (last !== undefined && now - last < this.options.retriggerMs) return 'throttled';
    if (this.voices >= this.options.maxVoices) return 'voices';
    this.lastPlayed.set(cue, now);
    if (def.synth) {
      this.synth(def.synth, def.gain, def.bus);
      return 'played';
    }
    const files = def.files ?? [];
    if (files.length === 0) return 'missing';
    const i = this.variant.get(cue) ?? 0;
    this.variant.set(cue, (i + 1) % files.length);
    const file = files[i % files.length];
    if (file === undefined) return 'missing';
    if (this.missing.has(file)) return 'missing';
    const cached = this.cache.get(file);
    if (cached) {
      cached.lastUsed = now;
      this.start(cached.buffer, def.gain, def.bus);
      return 'played';
    }
    const asked = now;
    void this.load(file).then((buffer) => {
      if (buffer && this.ports.now() - asked <= this.options.lateMs) {
        this.start(buffer, def.gain, def.bus);
        this.note(cue, 'played');
      }
    });
    return 'loading';
  }

  private start(buffer: BufferLike, gain: number, bus: 'sfx' | 'voice'): void {
    const ctx = this.ctx;
    const out = this.buses[bus];
    if (!ctx || !out) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(out);
    this.voices += 1;
    src.onended = () => {
      this.voices = Math.max(0, this.voices - 1);
      src.disconnect();
      g.disconnect();
    };
    src.start(0);
  }

  /** Code-synthesised UI blips (no file): a short enveloped sine. */
  private synth(kind: 'tap' | 'open' | 'close', gain: number, bus: 'sfx' | 'voice'): void {
    const ctx = this.ctx;
    const out = this.buses[bus];
    if (!ctx || !out) return;
    const t = ctx.currentTime;
    const [f0, f1, dur] =
      kind === 'tap' ? [880, 880, 0.04] : kind === 'open' ? [520, 780, 0.09] : [780, 520, 0.09];
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.linearRampToValueAtTime(f1, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.005);
    g.gain.linearRampToValueAtTime(0, t + dur);
    osc.connect(g);
    g.connect(out);
    osc.start(t);
    osc.stop(t + dur + 0.01);
  }

  /**
   * Move the music to a state (crossfade). A state with no cue, or whose file is missing, fades
   * the current track out and stays silent. Before unlock the state is remembered and starts on
   * unlock (`resumeMusic`).
   */
  setMusicState(state: MusicState): void {
    if (this.musicState === state) return;
    this.musicState = state;
    this.startMusic();
  }

  /** Start the remembered state's music (called after unlock). */
  resumeMusic(): void {
    if (this.musicState && !this.music) this.startMusic();
  }

  private startMusic(): void {
    const state = this.musicState;
    const ctx = this.ctx;
    const bus = this.buses.music;
    if (!state || !ctx || !bus || !this.unlocked) return;
    const cue = this.map.musicStates[state];
    const def = cue ? this.map.music[cue] : undefined;
    const fade = this.options.crossfadeSec;
    const t = ctx.currentTime;
    const old = this.music;
    if (old && old.cue === cue) return;
    if (old) {
      old.gain.gain.cancelScheduledValues(t);
      old.gain.gain.setValueAtTime(old.gain.gain.value, t);
      old.gain.gain.linearRampToValueAtTime(0, t + fade);
      const media = old.media;
      setTimeout(() => media.pause(), fade * 1000 + 50);
      this.music = null;
    }
    if (!def || !cue) {
      this.note(`music:${state}`, 'unknown');
      return;
    }
    if (this.missing.has(`music:${def.file}`)) {
      this.note(cue, 'missing');
      return;
    }
    const media = this.ports.createMedia();
    if (!media) return;
    media.preload = 'auto';
    media.loop = def.loop;
    media.src = fileUrl('music', def.file, this.ext, this.options.baseUrl);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(def.gain, t + fade);
    try {
      ctx.createMediaElementSource(media).connect(g);
    } catch {
      return;
    }
    g.connect(bus);
    const slot: MusicSlot = { media, gain: g, cue };
    media.addEventListener('error', () => {
      this.missing.add(`music:${def.file}`);
      this.note(cue, 'missing');
      if (this.music === slot) this.music = null;
    });
    this.music = slot;
    media.play().then(
      () => this.note(cue, 'played'),
      () => {
        /* the error listener records a missing file; a refused play stays silent */
      },
    );
  }
}
